import React, { useState, useEffect } from 'react';
import {
    Database,
    Download,
    Trash2,
    RefreshCw,
    AlertCircle,
    CheckCircle,
    Calendar,
    FileText,
    HardDrive,
    Upload,
} from 'lucide-react';
import { superadminApi } from '../../lib/superadminApi';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';

interface Backup {
    id: string;
    filename: string;
    size: number;
    createdAt: string;
    restoredAt?: string;
    restoredBy?: string;
}

interface BackupsStats {
    total: number;
    totalSize: number;
    recent: Backup[];
}

const BackupAdminPage: React.FC = () => {
    const [stats, setStats] = useState<BackupsStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [isRestoring, setIsRestoring] = useState<string | null>(null);
    const [isRestoringUpload, setIsRestoringUpload] = useState(false);
    const [isDeleting, setIsDeleting] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showConfirmRestore, setShowConfirmRestore] = useState<string | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);

    useEffect(() => {
        loadBackups();
    }, []);

    const loadBackups = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await superadminApi.listBackups();
            if (response.success && response.data) {
                setStats(response.data);
            } else {
                setError(response.message || 'Error al cargar los backups');
            }
        } catch (err) {
            setError('Error de conexión al cargar los backups');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateBackup = async () => {
        setIsCreating(true);
        setError('');
        setSuccessMessage('');
        try {
            const response = await superadminApi.createBackup();
            if (response.success) {
                setSuccessMessage('Backup creado exitosamente');
                setTimeout(() => setSuccessMessage(''), 3000);
                await loadBackups();
            } else {
                setError(response.message || 'Error al crear el backup');
            }
        } catch (err) {
            setError('Error de conexión al crear el backup');
        } finally {
            setIsCreating(false);
        }
    };

    const handleDownloadBackup = async (id: string, filename: string) => {
        try {
            const blob = await superadminApi.downloadBackup(id);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            setError('Error al descargar el backup');
        }
    };

    const handleRestoreBackup = async (id: string) => {
        setIsRestoring(id);
        setError('');
        setSuccessMessage('');
        try {
            const response = await superadminApi.restoreBackup(id);
            if (response.success) {
                setSuccessMessage('Backup restaurado exitosamente');
                setTimeout(() => setSuccessMessage(''), 3000);
                await loadBackups();
            } else {
                setError(response.message || 'Error al restaurar el backup');
            }
        } catch (err) {
            setError('Error de conexión al restaurar el backup');
        } finally {
            setIsRestoring(null);
            setShowConfirmRestore(null);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setUploadFile(file);
        }
    };

    const handleRestoreFromUpload = async () => {
        if (!uploadFile) {
            setError('Por favor selecciona un archivo');
            return;
        }

        setIsRestoringUpload(true);
        setError('');
        setSuccessMessage('');
        try {
            const response = await superadminApi.restoreFromUpload(uploadFile);
            if (response.success) {
                setSuccessMessage('Backup restaurado exitosamente');
                setTimeout(() => setSuccessMessage(''), 3000);
                setShowUploadModal(false);
                setUploadFile(null);
                await loadBackups();
            } else {
                setError(response.message || 'Error al restaurar el backup');
            }
        } catch (err) {
            setError('Error de conexión al restaurar el backup');
        } finally {
            setIsRestoringUpload(false);
        }
    };

    const handleDeleteBackup = async (id: string) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar este backup? Esta acción no se puede deshacer.')) {
            return;
        }

        setIsDeleting(id);
        setError('');
        try {
            const response = await superadminApi.deleteBackup(id);
            if (response.success) {
                await loadBackups();
            } else {
                setError(response.message || 'Error al eliminar el backup');
            }
        } catch (err) {
            setError('Error de conexión al eliminar el backup');
        } finally {
            setIsDeleting(null);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestión de Copias de Seguridad</h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Crea, descarga, restaura y elimina copias de seguridad de la base de datos.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={loadBackups}
                        variant="secondary"
                        disabled={isLoading}
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                        Actualizar
                    </Button>
                    <Button
                        onClick={handleCreateBackup}
                        variant="primary"
                        disabled={isCreating}
                    >
                        <Database className={`w-4 h-4 mr-2 ${isCreating ? 'animate-pulse' : ''}`} />
                        {isCreating ? 'Creando...' : 'Crear Backup'}
                    </Button>
                    <Button
                        onClick={() => setShowUploadModal(true)}
                        variant="outline"
                    >
                        <Upload className="w-4 h-4 mr-2" />
                        Subir Backup
                    </Button>
                </div>
            </div>

            {successMessage && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    {successMessage}
                </div>
            )}

            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="flex justify-center py-8">
                    <Loader size="lg" />
                </div>
            ) : stats && stats.recent.length > 0 ? (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Backups</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
                                </div>
                                <Database className="w-10 h-10 text-blue-500" />
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Tamaño Total</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {formatFileSize(stats.totalSize)}
                                    </p>
                                </div>
                                <HardDrive className="w-10 h-10 text-green-500" />
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Último Backup</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {stats.recent.length > 0 ? formatDate(stats.recent[0].createdAt) : 'N/A'}
                                    </p>
                                </div>
                                <Calendar className="w-10 h-10 text-purple-500" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Lista de Backups</h2>
                        <div className="space-y-4">
                            {stats.recent.map((backup) => (
                                <div
                                    key={backup.id}
                                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <FileText className="w-5 h-5 text-blue-500" />
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    {backup.filename}
                                                </h3>
                                                {backup.restoredAt && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                                                        Restaurado
                                                    </span>
                                                )}
                                            </div>
                                            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4" />
                                                    <span>Creado: {formatDate(backup.createdAt)}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <HardDrive className="w-4 h-4" />
                                                    <span>Tamaño: {formatFileSize(backup.size)}</span>
                                                </div>
                                                {backup.restoredAt && (
                                                    <div className="flex items-center gap-2">
                                                        <CheckCircle className="w-4 h-4" />
                                                        <span>Restaurado: {formatDate(backup.restoredAt)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 ml-4">
                                            <Button
                                                onClick={() => handleDownloadBackup(backup.id, backup.filename)}
                                                variant="secondary"
                                                size="sm"
                                                title="Descargar"
                                            >
                                                <Download className="w-4 h-4" />
                                            </Button>
                                            {!backup.restoredAt && (
                                                <Button
                                                    onClick={() => setShowConfirmRestore(backup.id)}
                                                    variant="outline"
                                                    size="sm"
                                                    title="Restaurar"
                                                    disabled={isRestoring === backup.id}
                                                >
                                                    <RefreshCw className={`w-4 h-4 ${isRestoring === backup.id ? 'animate-spin' : ''}`} />
                                                </Button>
                                            )}
                                            <Button
                                                onClick={() => handleDeleteBackup(backup.id)}
                                                variant="danger"
                                                size="sm"
                                                title="Eliminar"
                                                disabled={isDeleting === backup.id}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {showConfirmRestore && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
                                <div className="flex items-center mb-4">
                                    <AlertCircle className="w-6 h-6 text-yellow-500 mr-3" />
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Confirmar Restauración
                                    </h3>
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 mb-6">
                                    Esta acción sobrescribirá toda la base de datos con el contenido del backup seleccionado.
                                    <strong> Esta acción no se puede deshacer.</strong>
                                    <br /><br />
                                    ¿Estás seguro de que deseas continuar?
                                </p>
                                <div className="flex justify-end gap-3">
                                    <Button
                                        onClick={() => setShowConfirmRestore(null)}
                                        variant="secondary"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        onClick={() => handleRestoreBackup(showConfirmRestore)}
                                        variant="danger"
                                        disabled={isRestoring === showConfirmRestore}
                                    >
                                        {isRestoring === showConfirmRestore ? 'Restaurando...' : 'Restaurar Backup'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showUploadModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
                                <div className="flex items-center mb-4">
                                    <Upload className="w-6 h-6 text-blue-500 mr-3" />
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Subir Copia de Seguridad
                                    </h3>
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 mb-4">
                                    Selecciona un archivo SQL restaurado previamente.
                                    <br /><br />
                                    <strong> Esta acción sobrescribirá toda la base de datos y no se puede deshacer.</strong>
                                </p>
                                <div className="mb-4">
                                    <input
                                        type="file"
                                        accept=".sql"
                                        onChange={handleFileUpload}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-700 dark:text-white"
                                    />
                                    {uploadFile && (
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                            Archivo seleccionado: {uploadFile.name} ({formatFileSize(uploadFile.size)})
                                        </p>
                                    )}
                                </div>
                                <div className="flex justify-end gap-3">
                                    <Button
                                        onClick={() => {
                                            setShowUploadModal(false);
                                            setUploadFile(null);
                                        }}
                                        variant="secondary"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        onClick={handleRestoreFromUpload}
                                        variant="primary"
                                        disabled={!uploadFile || isRestoringUpload}
                                    >
                                        {isRestoringUpload ? 'Restaurando...' : 'Restaurar'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center py-12">
                    <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No hay copias de seguridad
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Crea tu primera copia de seguridad de la base de datos.
                    </p>
                    <Button
                        onClick={handleCreateBackup}
                        variant="primary"
                        disabled={isCreating}
                    >
                        <Database className={`w-4 h-4 mr-2 ${isCreating ? 'animate-pulse' : ''}`} />
                        {isCreating ? 'Creando...' : 'Crear Primer Backup'}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default BackupAdminPage;
